/**
 * Endless free-play verse packs: survival milestones + pity.
 * Progress is device-local (no account).
 */
import {
  applyArtPack,
  applyPackPick,
  buildPackOptions,
  hasAnyPackRewardLeft,
  isCollectionMaxed,
  lockedChapterArtIds,
  type PackGrantResult,
} from "../../../shared/collection/packs";
import { loadCollection } from "../../../shared/collection";

const KEY = "sots-endless-packs-v1";

/** Survive this long (seconds) to earn a pack on a normal run. */
export const PACK_SURVIVE_SECONDS = 45;
/** Short-run pity floor after several dry runs. */
export const PACK_PITY_SURVIVE_SECONDS = 22;
/** Dry runs before pity can fire. */
export const PACK_PITY_RUNS = 2;

export type EndlessPackSave = {
  version: 1;
  packsOpened: number;
  packsOffered: number;
  runsSincePack: number;
  totalRuns: number;
};

export type EndlessPackOffer =
  | {
      kind: "cards";
      options: string[];
      reason: "survive" | "pity";
      surviveSeconds: number;
    }
  | {
      kind: "art";
      reason: "survive" | "pity" | "collection-max";
      surviveSeconds: number;
    }
  | {
      kind: "none";
      reason: "too-short" | "complete" | "empty";
      surviveSeconds: number;
    };

function defaultSave(): EndlessPackSave {
  return {
    version: 1,
    packsOpened: 0,
    packsOffered: 0,
    runsSincePack: 0,
    totalRuns: 0,
  };
}

function loadSave(): EndlessPackSave {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultSave();
    const p = JSON.parse(raw) as Partial<EndlessPackSave>;
    return {
      version: 1,
      packsOpened: Math.max(0, Math.floor(Number(p.packsOpened) || 0)),
      packsOffered: Math.max(0, Math.floor(Number(p.packsOffered) || 0)),
      runsSincePack: Math.max(0, Math.floor(Number(p.runsSincePack) || 0)),
      totalRuns: Math.max(0, Math.floor(Number(p.totalRuns) || 0)),
    };
  } catch {
    return defaultSave();
  }
}

function writeSave(s: EndlessPackSave): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function getEndlessPackStats(): EndlessPackSave {
  return loadSave();
}

function qualify(
  survived: number,
  save: EndlessPackSave,
): "survive" | "pity" | null {
  if (survived >= PACK_SURVIVE_SECONDS) return "survive";
  if (
    save.runsSincePack >= PACK_PITY_RUNS &&
    survived >= PACK_PITY_SURVIVE_SECONDS
  ) {
    return "pity";
  }
  return null;
}

/**
 * Evaluate a finished endless run. Mutates pack progress counters.
 * Call once per run end (before showing UI).
 */
export function evaluateEndlessPack(survived: number): EndlessPackOffer {
  loadCollection(); // ensure migrated
  const save = loadSave();
  save.totalRuns += 1;
  const t = Math.max(0, survived);

  if (!hasAnyPackRewardLeft()) {
    save.runsSincePack += 1;
    writeSave(save);
    return { kind: "none", reason: "complete", surviveSeconds: t };
  }

  const q = qualify(t, save);
  if (!q) {
    save.runsSincePack += 1;
    writeSave(save);
    return { kind: "none", reason: "too-short", surviveSeconds: t };
  }

  // Consume eligibility for this offer
  save.packsOffered += 1;
  save.runsSincePack = 0;
  writeSave(save);

  const options = buildPackOptions(3);
  if (options.length > 0) {
    return {
      kind: "cards",
      options,
      reason: q,
      surviveSeconds: t,
    };
  }

  // Cards maxed (or only gated left) — art keepsake pack
  if (lockedChapterArtIds().length > 0) {
    return {
      kind: "art",
      reason: isCollectionMaxed() ? "collection-max" : q,
      surviveSeconds: t,
    };
  }

  return { kind: "none", reason: "empty", surviveSeconds: t };
}

/** Player took a card from the pack. */
export function claimEndlessPackCard(verseId: string): PackGrantResult {
  const result = applyPackPick(verseId);
  const save = loadSave();
  save.packsOpened += 1;
  writeSave(save);
  return result;
}

/** Player took pure art pack (collection full). */
export function claimEndlessArtPack(): string[] {
  const art = applyArtPack(1);
  const save = loadSave();
  if (art.length) save.packsOpened += 1;
  writeSave(save);
  return art;
}

/** Player skipped the pack UI (still counts as offered). */
export function skipEndlessPack(): void {
  // Counters already advanced in evaluateEndlessPack
}

export function packReasonLead(
  offer: Extract<EndlessPackOffer, { kind: "cards" | "art" }>,
): string {
  if (offer.kind === "art") {
    if (offer.reason === "collection-max") {
      return "Your arsenal is full (×4). A gallery keepsake is ready instead.";
    }
    return "No new verses available — claim a gallery keepsake.";
  }
  if (offer.reason === "pity") {
    return "A verse pack for staying the course — choose one for your collection.";
  }
  return "Survived long enough for a verse pack — choose one for your collection.";
}
